import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import React from 'react';
import { CMS_THEME } from '../../common/enums';
import { proseLinkStyles } from '../Link';
import { processAndAttachLinkTracking } from './utils';

const styles = pdsTv({
  base: [
    'pds-prose pds-max-w-none pds-overflow-auto ',
    'prose-ol:pds-ml-4 prose-ul:pds-ml-4 md:prose-ol:pds-ml-5 md:prose-ul:pds-ml-5',
    ...proseLinkStyles,

    // blockquote
    'prose-blockquote:pds-border-[0px] prose-blockquote:pds-border-l-thick prose-blockquote:pds-border-solid prose-blockquote:pds-border-l-primary prose-blockquote:pds-pl-4.5 prose-blockquote:pds-font-normal [&_>_*_+_*]:pds-mb-1 prose-blockquote:[&last-child]:pds-mb-0',

    'prose-h1:!pds-mb-4 prose-h2:!pds-mb-4 prose-h3:!pds-mb-4 prose-h4:!pds-mb-4 prose-h5:!pds-mb-4',

    // custom alignment classes
    '[&_.text-align-center]:pds-text-center [&_.text-align-right]:pds-text-right',

    // tables
    'prose-table:pds-table-auto prose-table:pds-overflow-hidden prose-table:pds-rounded prose-th:pds-bg-surface-layer prose-th:pds-pl-3 prose-td:pds-py-3',

    // images
    'prose-img:pds-mx-auto prose-img:pds-block',

    // images - first child
    '[&_.media--type-image:first-child_img]:pds-mt-0',

    // images - floated
    'lg:[&_.align-left]:pds-float-left lg:[&_.align-left]:pds-mr-5 [&_.align-left_img]:pds-m-0',
    'lg:[&_.align-right]:pds-float-right lg:[&_.align-right]:pds-ml-5 [&_.align-right_img]:pds-m-0',

    // images - floated - ul/ol siblings
    'lg:[&_.align-left:has(+_ul_,_+_ol)]:pds-mr-6',
    'lg:[&_.align-right:has(+_ul_,_+_ol)]:pds-mr-6',

    // images - floated - first sibling if floated images is at the top
    'lg:[&:has(.align-left:first-child)_>:nth-child(2)]:pds-mt-0',
    'lg:[&:has(.align-right:first-child)_>:nth-child(2)]:pds-mt-0',

    // nerd two column layout
    // String.raw allows the escape sequences to work correctly - https://github.com/tailwindlabs/tailwindcss/issues/8881
    String.raw`[&_.drupal-layout\_\_column_*:first-child]:pds-mt-0`,
    String.raw`[&_.drupal-layout\_\_column_*:last-child]:pds-mb-0`,
  ],
  variants: {
    theme: {
      dark: [
        'pds-prose-invert',
        'hover:prose-a:pds-text-focus-inverse',
        'focus-visible:prose-a:pds-text-focus-inverse focus-visible:prose-a:pds-outline-focus-inverse',
        'active:prose-a:pds-text-surface active:prose-a:pds-outline-surface',
      ],
      light: '',
      neutral: '',
    },
    isArticle: {
      true: 'rich-article [&_p:first-of-type]:pds-text-title-sm',
    },
  },
});

export type RichTextProps = {
  body?: string;
  isArticle?: boolean;
  className?: string;
  cmsTheme?: CMS_THEME;
  children?: React.ReactNode;
  analytics?: boolean;
} & ({ body: string } | { children: React.ReactNode });

const RichText = ({
  body,
  isArticle,
  className,
  cmsTheme = CMS_THEME.LIGHT,
  analytics = true,
  children,
}: RichTextProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const wysiwyg = styles({
    theme: cmsTheme,
    isArticle,
    className,
  });

  React.useEffect(() => {
    if (body && analytics && containerRef.current) {
      const cleanup = processAndAttachLinkTracking(containerRef);
      return cleanup;
    }
    return undefined; // don't like this but was getting a warning
  }, [analytics, body]);

  if (children) {
    return <div className={wysiwyg}>{children}</div>;
  }

  if (body) {
    return (
      <div
        ref={containerRef}
        className={wysiwyg}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }

  return null;
};

export default RichText;
